import { ObjectMapper } from "json-object-mapper";
import { IModel } from "../IModel";
import { Model } from "../model";
import { PipelineConfigType } from "./enum/PipelineConfigType.enum";
import { RecruitmentCandidateStatus } from "./enum/RecruitmentCandidateStatus.enum";
import { RecruitmentStatus } from "./enum/RecruitmentStatus.enum";
import { RecruitPipelineAction } from "./recruit-pipeline-action.model";

export class RecruitPipelineConfigModel extends Model implements IModel<RecruitPipelineConfigModel> {

    public title: string = "";
    public companyGroupId: string = "";
    public type: number = PipelineConfigType.RECRUITMENT.id;
    public requiredSchedule: boolean = false;
    public statusMapping: number = RecruitmentCandidateStatus.IN_PROGRESS.id;
    public order: number = 0;
    public isUpdate: boolean = false;

    public emailActive?: boolean = false;
    public whatsappActive?: boolean = false;
    public emailSubject?: string = "";
    public emailBody?: string = "";
    public whatsappMessage?: string = "";
    public lastModified?: string = "";
    public actions: RecruitPipelineAction[] = [];
    public _selectedActionsCache?: string[]; 

    private _originalValues: any = {};

    public get typeData() {
        if (typeof this.type === 'number') {
            return PipelineConfigType.getById(this.type);
        } else if (typeof this.type === 'string') {
            return PipelineConfigType.getBy(this.type as any);
        }
        return null;
    }

    public get statusMappingData() {
        const statusId = typeof this.statusMapping === 'number'
            ? this.statusMapping
            : Number(this.statusMapping);

        const pipelineTypeId = this.getPipelineTypeId();

        if (pipelineTypeId === PipelineConfigType.CANDIDATE.id) {
            return RecruitmentCandidateStatus.getById(statusId);
        }

        return RecruitmentStatus.getById(statusId);
    }

    deserialize(input: any): RecruitPipelineConfigModel {
        if (!input) {
            return this;
        }

        Object.assign(this, input);

        if (input.requiredSchedule !== undefined) {
            this.requiredSchedule = Boolean(input.requiredSchedule);
        }

        const pipelineTypeSource = input.type !== undefined ? input.type : this.type;
        this.type = this.resolvePipelineType(pipelineTypeSource);

        const statusMappingSource = input.statusMapping ?? input.candidateStatusMapping ?? input.recruitmentStatusMapping ?? this.statusMapping;
        this.statusMapping = this.resolveStatusMapping(statusMappingSource);

        if (input.order !== undefined) {
            this.order = Number(input.order);
        }

        this.isUpdate = input.isUpdate || false;

        const actionsData = input.recruitPipelineActions || input.actions;
        if (actionsData !== undefined && Array.isArray(actionsData)) {
            this.actions = RecruitPipelineAction.fromArray(actionsData);
            this.actions.forEach(action => {
                if (this.id && !action.pipelineConfigId) {
                    action.pipelineConfigId = this.id;
                }
            });
            this._selectedActionsCache = this.actions.map(a => a.action);
        } else {
            this.actions = [];
            this._selectedActionsCache = [];
        }

        this.saveOriginalValues();

        return this;
    }

    private saveOriginalValues(): void {
        this._originalValues = {
            title: this.title,
            type: this.type,
            requiredSchedule: this.requiredSchedule,
            statusMapping: this.statusMapping,
            order: this.order,
            emailActive: this.emailActive,
            whatsappActive: this.whatsappActive,
            actions: JSON.parse(JSON.stringify(this.actions))
        };
    }

    public markAsModified(): void {
        this.isUpdate = true;
    }

    public hasChanges(): boolean {
        const actionsChanged = JSON.stringify(this.actions.map(a => ({ id: a.id, action: a.action }))) !== 
                              JSON.stringify((this._originalValues.actions || []).map((a: any) => ({ id: a.id, action: a.action })));
        
        return this.title !== this._originalValues.title ||
               this.type !== this._originalValues.type ||
               this.requiredSchedule !== this._originalValues.requiredSchedule ||
               this.statusMapping !== this._originalValues.statusMapping ||
               this.emailActive !== this._originalValues.emailActive ||
               this.whatsappActive !== this._originalValues.whatsappActive ||
               actionsChanged;
    }

    serialize(): any {
        const obj: any = {};

        obj.title = this.title || '';
        obj.companyGroupId = this.companyGroupId || '';
        
        const typeId = this.getPipelineTypeId();
        if (typeId && typeId > 0) {
            obj.type = typeId;
        }
        
        const statusMappingId = this.resolveStatusMapping(this.statusMapping);
        if (statusMappingId !== undefined && statusMappingId !== null) {
            obj.statusMapping = statusMappingId;
        }
        
        obj.requiredSchedule = this.requiredSchedule === true;
        obj.order = this.order || 0;
        obj.isUpdate = this.isUpdate;

        if (this.id) {
            obj.id = this.id;
        }

        if (this.emailActive === true || this.emailActive === false) {
            obj.emailActive = this.emailActive;
        }
        if (this.whatsappActive === true || this.whatsappActive === false) {
            obj.whatsappActive = this.whatsappActive;
        }
        if (this.emailSubject) {
            obj.emailSubject = this.emailSubject;
        }
        if (this.emailBody) {
            obj.emailBody = this.emailBody;
        }
        if (this.whatsappMessage) {
            obj.whatsappMessage = this.whatsappMessage;
        }

        if (this.actions && this.actions.length > 0) {
            obj.actions = this.actions.map(action => action.serialize());
        } else {
            obj.actions = [];
        }

        return obj;
    }

    public static fromArray(input: any[]): RecruitPipelineConfigModel[] {
        if (!input || !Array.isArray(input)) {
            return [];
        }
        return input.map(item => new RecruitPipelineConfigModel().deserialize(item));
    }

    private resolveStatusMapping(rawValue: any): number {
        const pipelineTypeId = this.getPipelineTypeId();
        const defaultValue = this.getDefaultStatusMappingFor(pipelineTypeId);

        if (rawValue === undefined || rawValue === null || rawValue === '') {
            return defaultValue;
        }

        if (typeof rawValue === 'object') {
            const objectId = Number((rawValue as any).id);
            if (!isNaN(objectId)) {
                return objectId;
            }

            if ((rawValue as any).value !== undefined) {
                rawValue = (rawValue as any).value;
            }
        }

        if (typeof rawValue === 'number' && !isNaN(rawValue)) {
            return rawValue;
        }

        const numeric = Number(rawValue);
        if (!isNaN(numeric)) {
            return numeric;
        }

        const normalized = String(rawValue).trim();
        if (!normalized) {
            return defaultValue;
        }

        if (pipelineTypeId === PipelineConfigType.CANDIDATE.id) {
            const candidateStatus = this.findCandidateStatus(normalized);
            return candidateStatus?.id ?? defaultValue;
        }

        const recruitmentStatus = this.findRecruitmentStatus(normalized);
        return recruitmentStatus?.id ?? defaultValue;
    }

    private resolvePipelineType(rawValue: any): number {
        if (rawValue === undefined || rawValue === null || rawValue === '') {
            return PipelineConfigType.RECRUITMENT.id;
        }

        if (typeof rawValue === 'object') {
            const objectId = Number((rawValue as any).id);
            if (!isNaN(objectId)) {
                return objectId;
            }

            if ((rawValue as any).value !== undefined) {
                rawValue = (rawValue as any).value;
            }
        }

        if (typeof rawValue === 'number' && !isNaN(rawValue)) {
            return rawValue;
        }

        const numeric = Number(rawValue);
        if (!isNaN(numeric)) {
            return numeric;
        }

        const normalized = String(rawValue).trim();
        if (!normalized) {
            return PipelineConfigType.RECRUITMENT.id;
        }

        const normalizedUpper = normalized.toUpperCase();
        const pipelineType = PipelineConfigType.getBy(normalizedUpper);
        if (pipelineType) {
            return pipelineType.id;
        }

        return PipelineConfigType.RECRUITMENT.id;
    }

    private getPipelineTypeId(): number {
        return this.resolvePipelineType(this.type);
    }

    private getDefaultStatusMappingFor(pipelineTypeId: number): number {
        return pipelineTypeId === PipelineConfigType.CANDIDATE.id
            ? RecruitmentCandidateStatus.IN_PROGRESS.id
            : RecruitmentStatus.DRAFT.id;
    }

    private findCandidateStatus(value: string): RecruitmentCandidateStatus | undefined {
        return RecruitmentCandidateStatus.getBy(value)
            || RecruitmentCandidateStatus.buildListEnum()
                .find(item => item.value?.toUpperCase() === value.toUpperCase()) as RecruitmentCandidateStatus | undefined;
    }

    private findRecruitmentStatus(value: string): RecruitmentStatus | undefined {
        return RecruitmentStatus.getBy(value)
            || RecruitmentStatus.buildListEnum()
                .find(item => item.value?.toUpperCase() === value.toUpperCase()) as RecruitmentStatus | undefined;
    }
}

